/**
 * Browser-side ring buffers of recent UI events. The bug-report submit flow
 * attaches their snapshots — chatboard's server uploads them to GCS (gated by
 * BUG_REPORT_CLIENT_LOG_BUFFER=true) and stores the URI on the bug_reports row.
 *
 * Two rings share one seq ledger (lib/telemetry/seqLedger.js):
 *   - `ringBuffer` (200 entries): domain events and the text group (`chat` /
 *     `submit` / `text`). Written to sessionStorage on every push, under a
 *     512 KB cap that stubs the oldest text entries in the stored copy.
 *   - `traceRing` (1,000 entries): `action`, `key`, `scroll`, `viewport`, `net`,
 *     `stream`, `store`, `console`. Written on an idle callback (at most every
 *     5 s) and on pagehide, into what the 2 MB storage budget leaves after the
 *     domain ring; it gives way first.
 *
 * sessionStorage is intentional — per-tab scope keeps reload-resume context
 * linear without cross-tab event interleaving from unrelated chatboard tabs. On
 * boot the rings are restored and a synthetic `ui.page.reload` event is appended
 * so triagers see the gap; unsent entries that did not survive (a trace-ring
 * write that never happened) are counted as dropped.
 *
 * Telemetry stream (services/telemetry.js): every entry is stamped with a per-tab
 * monotonic `seq` and `t_mono` at push. A seq is never reused within a tab: the
 * server derives event ids from it. The stream reads both rings through
 * `telemetryLog`, merged by seq.
 *
 * Sizes are counted in UTF-16 code units, which is what sessionStorage quotas count.
 */

import { pathAndSearch, sanitizeRestoredEntry, sanitizeUiEvent } from "./telemetry/sanitize.js";
import { SeqLedger, TELEMETRY_TAB_KEY } from "./telemetry/seqLedger.js";
import { guarded } from "./telemetry/guard.js";

export { TELEMETRY_TAB_KEY };

const STORAGE_KEY = "sooth.chatboard.log-ring";
export const TRACE_STORAGE_KEY = "sooth.chatboard.trace-ring";
const STORAGE_VERSION = 1;
/** Cap on the domain ring's stored copy. */
export const DOMAIN_STORAGE_CAP = 512 * 1024;
/** Both rings together; leaves room for the forecast draft and other keys. */
export const STORAGE_BUDGET = 2 * 1024 * 1024;
/** Longest the trace ring waits for an idle callback before it writes anyway. */
const DEFERRED_PERSIST_MS = 5_000;
/** Entry fields that hold user text, emptied first when the stored copy is over its cap. */
const TEXT_FIELDS = ["text", "value", "question", "description"];

/**
 * Correlation fields a stub keeps: short ids the server attributes a row by (its visit,
 * the registry it was built against, the organisation it was captured under).
 */
const STUB_CONTEXT = ["visit_id", "registry_version", "identity_org"];

/** The core fields of an entry, stored in place of one trimmed for space. */
function stub(entry) {
  const { event, seq, t_mono, captured_at, view } = entry;
  const out = { event, seq, t_mono, captured_at, view };
  for (const k of STUB_CONTEXT) if (entry[k] !== undefined) out[k] = entry[k];
  return out;
}

const hasText = (entry) => TEXT_FIELDS.some((k) => typeof entry[k] === "string");

function defaultScheduler() {
  if (typeof window === "undefined") return null;
  return (fn) => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(fn, { timeout: DEFERRED_PERSIST_MS });
    } else {
      setTimeout(fn, DEFERRED_PERSIST_MS);
    }
  };
}

export class LogRingBuffer {
  /**
   * @param {number} capacity
   * @param {object} [opts]
   * @param {SeqLedger} [opts.ledger] shared ledger; a ring built without one owns its own
   * @param {string} [opts.name] ring name in `telemetry.persist_failed`
   * @param {string} [opts.storageKey]
   * @param {boolean} [opts.deferred] write on an idle callback instead of every push
   * @param {number | (() => number)} [opts.storageCap] stored-copy size cap
   * @param {boolean} [opts.hydrate] restore from storage now (false when a caller
   *   restores several rings together through `hydrateRings`)
   */
  constructor(
    capacity = 200,
    {
      ledger = null,
      name = "domain",
      storageKey = STORAGE_KEY,
      deferred = false,
      storageCap = DOMAIN_STORAGE_CAP,
      scheduler = defaultScheduler(),
      hydrate = true,
    } = {},
  ) {
    this.capacity = capacity;
    this.name = name;
    this.storageKey = storageKey;
    this.deferred = deferred;
    this.storageCap = storageCap;
    this.scheduler = scheduler;
    this.buffer = [];
    // Each entry's serialized form, kept beside it so a write never re-serializes
    // the whole ring.
    this._json = [];
    this._size = 0;
    /** Size of the last successful write (the trace ring's budget reads the domain's). */
    this.storedSize = 0;
    this._dirty = false;
    this._scheduled = false;
    // Ambient context merged into every pushed event so the triager can see
    // which forecast / trace the user was on for each captured event, without
    // having to remember to thread the id through every recordUiEvent call
    // site. Updated via setContext (typically from a useEffect mirroring
    // React state) and intentionally additive — the event's own keys take
    // precedence on collision.
    this.context = {};
    this.subscribers = new Set();
    this.ledger = ledger || new SeqLedger();
    this.ledger.rings.push(this);
    if (hydrate) hydrateRings(this.ledger, [this]);
  }

  // The ledger's counters, read through the ring for callers that use one ring.
  get lastAckedSeq() {
    return this.ledger.lastAckedSeq;
  }
  get nextSeq() {
    return this.ledger.nextSeq;
  }
  get discardedThrough() {
    return this.ledger.discardedThrough;
  }
  get hydratedThroughSeq() {
    return this.ledger.hydratedThroughSeq;
  }
  get droppedCount() {
    return this.ledger.droppedCount;
  }
  get autoAck() {
    return this.ledger.autoAck;
  }
  set autoAck(v) {
    this.ledger.autoAck = v;
  }

  /**
   * Merge fields into the ambient context. Keys accumulate across calls from
   * independent writers (the active-row effect and the contract-key recorder)
   * rather than replacing each other. To update a specific key pass it with
   * its new or null value. Note `clear()` drops buffered events only and does
   * NOT reset this context — clear a context key by passing it as null. Fields
   * set here are merged into every subsequent push() unless the event explicitly sets
   * the same key (event keys win, so a forecast-targeted event can override
   * the ambient active id).
   */
  setContext(ctx) {
    if (ctx && typeof ctx === "object" && !Array.isArray(ctx)) {
      this.context = { ...this.context, ...ctx };
    }
  }

  /** The stored payload, or null when absent or unreadable (cleared so it never re-trips). */
  _load() {
    const storage = storageOrNull();
    if (!storage) return null;
    let parsed;
    let raw = null;
    try {
      raw = storage.getItem(this.storageKey);
      if (!raw) return null;
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
    if (!parsed || parsed.v !== STORAGE_VERSION || !Array.isArray(parsed.events)) {
      try {
        storage.removeItem(this.storageKey);
      } catch {
        /* best effort */
      }
      return null;
    }
    // What is already stored counts against the shared budget before the next write.
    this.storedSize = raw.length;
    return parsed;
  }

  /** Replace the buffer with restored entries (already sanitized). */
  _restore(entries) {
    this.buffer = entries.slice(-this.capacity);
    this._json = this.buffer.map((e) => JSON.stringify(e));
    this._size = this._json.reduce((n, j) => n + j.length, 0);
  }

  _cap() {
    const c = typeof this.storageCap === "function" ? this.storageCap() : this.storageCap;
    return Math.max(0, c);
  }

  /**
   * Write the stored copy now. Over the cap, the oldest text entries are stubbed
   * first, then the oldest entries left out; memory keeps everything.
   */
  flushPersist() {
    this._dirty = false;
    const storage = storageOrNull();
    if (!storage) return;
    const cap = this._cap();
    const head = `{"v":${STORAGE_VERSION},"events":[`;
    let parts = this._json;
    // The payload is the entries plus the wrapper and one comma between each two.
    let size = this._size + head.length + 2 + Math.max(0, parts.length - 1);
    if (size > cap) {
      parts = parts.slice();
      for (let i = 0; i < parts.length && size > cap; i++) {
        if (!hasText(this.buffer[i])) continue;
        const s = JSON.stringify(stub(this.buffer[i]));
        size -= parts[i].length - s.length;
        parts[i] = s;
      }
      let first = 0;
      while (first < parts.length && size > cap) size -= parts[first++].length + 1;
      parts = parts.slice(first);
      // Dropping the last entry removed no comma with it.
      if (parts.length === 0) size = head.length + 2;
    }
    const payload = `${head}${parts.join(",")}]}`;
    try {
      storage.setItem(this.storageKey, payload);
      this.storedSize = payload.length;
    } catch {
      // Quota or storage disabled: the entries are still in memory and still sent.
      this.ledger.notePersistFailed(this.name);
    }
  }

  _persist() {
    if (!this.deferred || !this.scheduler) {
      this.flushPersist();
      return;
    }
    this._dirty = true;
    if (this._scheduled) return;
    this._scheduled = true;
    this.scheduler(() => {
      this._scheduled = false;
      if (this._dirty) this.flushPersist();
    });
  }

  /** Write a pending deferred copy now (pagehide). */
  flushIfDirty() {
    if (this._dirty) this.flushPersist();
  }

  push(event) {
    guarded(() => this._push(event));
  }

  _push(event) {
    const clean = sanitizeUiEvent(event);
    const stamped = {
      // Context first so explicit event keys override ambient values on
      // collision (e.g. ui.forecast.switch sets `from_id`/`to_id` directly,
      // doesn't want the ambient active_id to mask either of them).
      ...this.context,
      ...clean,
      captured_at: clean.captured_at || new Date().toISOString(),
      seq: this.ledger.allocate(),
      // An entry queued before the first /api/me answer (lib/telemetry/groups.js) keeps
      // its capture time; its seq is the push order.
      t_mono: Number.isFinite(clean.t_mono)
        ? clean.t_mono
        : typeof performance !== "undefined"
          ? performance.now()
          : null,
    };
    const json = JSON.stringify(stamped);
    this.buffer.push(stamped);
    this._json.push(json);
    this._size += json.length;
    if (this.buffer.length > this.capacity) {
      const n = this.buffer.length - this.capacity;
      const evicted = this.buffer.splice(0, n);
      for (const j of this._json.splice(0, n)) this._size -= j.length;
      const unsent = evicted.filter((e) => e.seq > this.ledger.lastAckedSeq);
      if (unsent.length > 0) this.ledger.noteEvictedSeqs(unsent.map((e) => e.seq));
    }
    this._persist();
    for (const fn of this.subscribers) {
      try {
        fn(stamped);
      } catch {
        /* a subscriber must never break event capture */
      }
    }
  }

  /** Call `fn(entry)` after every push; returns an unsubscribe function. */
  subscribe(fn) {
    this.subscribers.add(fn);
    return () => this.subscribers.delete(fn);
  }

  /** Buffered entries the stream has not acknowledged (`seq > last_acked_seq`). */
  unacked() {
    const w = this.ledger.lastAckedSeq;
    return this.buffer.filter((e) => e.seq > w);
  }

  /** Seq of the oldest unacknowledged entry, or Infinity. */
  firstUnackedSeq() {
    const w = this.ledger.lastAckedSeq;
    const e = this.buffer.find((x) => x.seq > w);
    return e ? e.seq : Infinity;
  }

  ack(seq) {
    this.ledger.ack(seq);
  }

  rewindAck(seq) {
    this.ledger.rewindAck(seq);
  }

  ackAll() {
    this.ledger.ackAll();
  }

  recordDropped(count, throughSeq) {
    this.ledger.recordDropped(count, throughSeq);
  }

  takeDropped() {
    return this.ledger.takeDropped();
  }

  noteSkipped(count, throughSeq) {
    this.ledger.noteSkipped(count, throughSeq);
  }

  takeSkipped() {
    return this.ledger.takeSkipped();
  }

  isCounted(seq) {
    return this.ledger.isCounted(seq);
  }

  pruneCounted() {
    this.ledger.pruneCounted();
  }

  snapshot() {
    // Spread inner objects so caller can't mutate our state.
    return this.buffer.map((e) => ({ ...e }));
  }

  /**
   * Drop all buffered events (in-memory AND the sessionStorage mirror) without
   * touching ambient context. Lets a triager/dev reset the log mid-session — no
   * reload — so the next captured bundle only holds events from a fresh repro.
   */
  clear() {
    const removed = this.unacked().map((e) => e.seq);
    this.buffer = [];
    this._json = [];
    this._size = 0;
    // The counters persist: a seq must never be reused within a tab. Cleared
    // entries count as acknowledged — the stream never sends them — but the
    // watermark stops below the other ring's oldest unsent entry.
    const other = this.ledger.lowestUnackedExcept(this);
    this.ledger.ack(Math.min(this.ledger.nextSeq - 1, other - 1));
    // Cleared seqs the watermark could not pass are recorded, so a reload does not
    // count them as lost.
    this.ledger.noteCleared(removed.filter((s) => s > this.ledger.lastAckedSeq));
    this.flushPersist();
  }
}

function storageOrNull() {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    return null;
  }
}

/**
 * Restore `rings` (all on `ledger`) from storage: counters first, then entries; an
 * unsent seq neither ring restored is counted as dropped, and the first ring gets
 * the `ui.page.reload` boundary event.
 */
export function hydrateRings(ledger, rings) {
  if (!storageOrNull()) return;
  const loaded = rings.map((r) => r._load());
  // A payload from before the ledger carried the counters itself.
  const legacy = loaded.find((p) => p && Number.isInteger(p.next_seq)) || null;
  ledger.hydrate(legacy);
  rings.forEach((r, i) => {
    const events = (loaded[i]?.events || [])
      .filter((e) => e && typeof e === "object")
      .map(sanitizeRestoredEntry);
    // Entries persisted before seq stamping: number them and treat them as already
    // sent, so a deploy never streams a pre-telemetry backlog.
    let numbered = false;
    for (const e of events) {
      if (!Number.isInteger(e.seq)) {
        e.seq = ledger.nextSeq++;
        numbered = true;
      }
    }
    if (numbered) ledger.lastAckedSeq = ledger.nextSeq - 1;
    r._restore(events);
    // A stale counter (its last write failed while the ring's survived) must never
    // hand out a seq a restored entry already holds.
    for (const e of r.buffer) if (e.seq >= ledger.nextSeq) ledger.nextSeq = e.seq + 1;
  });
  ledger.hydratedThroughSeq = ledger.nextSeq - 1;
  countLostOnReload(ledger, rings);
  const prior = rings.reduce((n, r) => n + r.buffer.length, 0);
  if (prior > 0) {
    // Synthetic boundary event so the reload is visible in triage rather
    // than appearing as a void between the last pre-reload event and the
    // first post-reload one.
    rings[0].push({
      event: "ui.page.reload",
      prior_events_count: prior,
      url: typeof window !== "undefined" ? pathAndSearch(window.location) : null,
    });
  }
}

/**
 * Unsent seqs no ring restored (a write that never happened), less the ones already
 * counted as evicted.
 */
function countLostOnReload(ledger, rings) {
  const floor = ledger.lastAckedSeq;
  const top = ledger.nextSeq - 1;
  if (top <= floor) return;
  const present = new Set();
  for (const r of rings) for (const e of r.buffer) if (e.seq > floor) present.add(e.seq);
  const lost = [];
  for (let s = floor + 1; s <= top; s++) {
    if (!present.has(s) && !ledger.isCounted(s) && !ledger.isCleared(s)) lost.push(s);
  }
  ledger.noteEvictedSeqs(lost);
}

/**
 * The stream's view of both rings (services/telemetry.js): pending entries merged
 * by seq, the shared watermark, and subscriptions to either ring.
 */
export class TelemetryLog {
  constructor(ledger, rings) {
    this.ledger = ledger;
    this.rings = rings;
  }
  get lastAckedSeq() {
    return this.ledger.lastAckedSeq;
  }
  get hydratedThroughSeq() {
    return this.ledger.hydratedThroughSeq;
  }
  get autoAck() {
    return this.ledger.autoAck;
  }
  set autoAck(v) {
    this.ledger.autoAck = v;
  }
  /** Service events (`telemetry.*`, `perf.summary`) go to the domain ring. */
  push(event) {
    this.rings[0].push(event);
  }
  unacked() {
    const lists = this.rings.map((r) => r.unacked());
    const nonEmpty = lists.filter((l) => l.length > 0);
    if (nonEmpty.length <= 1) return nonEmpty[0] || [];
    return nonEmpty.flat().sort((a, b) => a.seq - b.seq);
  }
  subscribe(fn) {
    const offs = this.rings.map((r) => r.subscribe(fn));
    return () => offs.forEach((off) => off());
  }
  ack(seq) {
    this.ledger.ack(seq);
  }
  rewindAck(seq) {
    this.ledger.rewindAck(seq);
  }
  ackAll() {
    this.ledger.ackAll();
  }
  recordDropped(count, throughSeq) {
    this.ledger.recordDropped(count, throughSeq);
  }
  takeDropped() {
    return this.ledger.takeDropped();
  }
  takePersistFailed() {
    return this.ledger.takePersistFailed();
  }
  noteSkipped(count, throughSeq) {
    this.ledger.noteSkipped(count, throughSeq);
  }
  takeSkipped() {
    return this.ledger.takeSkipped();
  }
  isCounted(seq) {
    return this.ledger.isCounted(seq);
  }
  pruneCounted() {
    this.ledger.pruneCounted();
  }
}

const ledger = new SeqLedger();
export const ringBuffer = new LogRingBuffer(200, { ledger, hydrate: false });
export const traceRing = new LogRingBuffer(1000, {
  ledger,
  name: "trace",
  storageKey: TRACE_STORAGE_KEY,
  deferred: true,
  storageCap: () => STORAGE_BUDGET - ringBuffer.storedSize,
  hydrate: false,
});
hydrateRings(ledger, [ringBuffer, traceRing]);
export const telemetryLog = new TelemetryLog(ledger, [ringBuffer, traceRing]);

if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  window.addEventListener("pagehide", () => traceRing.flushIfDirty());
}

// Dev/triage convenience: clear the UI-event rings from the console
// (`__clearLogRing()`) so an iterative repro doesn't accumulate a giant bundle.
// Gated to dev builds (import.meta.env.DEV) so it doesn't ship a global onto
// production builds — consistent with other dev-only gates in this codebase.
if (typeof window !== "undefined" && import.meta.env && import.meta.env.DEV) {
  window.__clearLogRing = () => {
    clearLogRings();
    return "log ring cleared";
  };
}

/** Clear both rings (the account modal's RESET LOG and `__clearLogRing`). */
export function clearLogRings() {
  ringBuffer.clear();
  traceRing.clear();
}

export function recordUiEvent(event) {
  ringBuffer.push(event);
}

/**
 * Snapshot the timeline container's scroll offset, the two side-drawer
 * geometries, and the viewport dims as a single `ui.layout_snapshot` event.
 * Layout bugs are otherwise invisible in the UI-event stream: bug 485fb71d
 * (Forecast Log rendered "half open") was a stray `scrollLeft` on the timeline
 * container dragging the absolute-positioned drawers off-screen — nothing in
 * the event log revealed it, so it needed live reproduction to diagnose.
 * Recording this into the bug-report bundle lets a triager see the exact
 * rendered geometry at report time. Fully guarded (DOM reads wrapped, SSR/node
 * short-circuit) so it can never throw in the submit path.
 */
export function captureLayoutSnapshot() {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return { event: "ui.layout_snapshot", unavailable: true };
  }
  const snap = {
    event: "ui.layout_snapshot",
    viewport: {
      innerW: window.innerWidth,
      innerH: window.innerHeight,
      // outerW/H catch the test-harness / zoom case where the emulated viewport
      // is wider than the real window and content spills off-screen.
      outerW: window.outerWidth,
      outerH: window.outerHeight,
    },
  };
  try {
    const container = document.querySelector('[data-testid="forecast-timeline"]');
    if (container) {
      // scrollLeft is the smoking gun for the drawer-shift class: it should
      // always be 0 (the container must not be horizontally scrollable).
      snap.timeline = {
        scrollLeft: container.scrollLeft,
        scrollTop: container.scrollTop,
        scrollWidth: container.scrollWidth,
        clientWidth: container.clientWidth,
      };
    }
    // Each drawer: rendered left-edge x, width, opacity, and open state. `open`
    // comes from the `inert` attribute (OverlayPanel sets inert while closed) —
    // reliable even mid-slide, unlike opacity which tweens 0↔1 during the 0.3s
    // transition and would report a half-closed drawer as open. Opacity is kept
    // (as a number) for finer triage. x far from the expected edge flags a
    // mis-render (the drawer-shift bug put an open panel at a negative x).
    const measure = (sel) => {
      const el = document.querySelector(sel);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return {
        x: Math.round(r.left),
        width: Math.round(r.width),
        opacity: Number(window.getComputedStyle(el).opacity),
        open: !el.hasAttribute("inert"),
      };
    };
    snap.forecastLog = measure('[data-testid="forecast-log"]');
    snap.chatConsole = measure('[data-testid="sayer-chat"]');
  } catch {
    // Any DOM-read failure is non-fatal — keep the viewport dims we already have.
    snap.error = true;
  }
  return snap;
}

export function setUiContext(ctx) {
  ringBuffer.setContext(ctx);
  traceRing.setContext(ctx);
}
